import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, TextField, Alert } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import deLocale from "date-fns/locale/de";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";

// Validation schema
const schema = yup.object().shape({
    vorname: yup.string().required("Vorname ist erforderlich."),
    nachname: yup.string().required("Nachname ist erforderlich."),
    telefon: yup
        .string()
        .matches(
            /^\+?[\d\s-]+$/,
            "Bitte geben Sie eine gültige Telefonnummer ein.",
        )
        .required("Telefonnummer ist erforderlich."),
    email: yup
        .string()
        .email("Bitte geben Sie eine gültige E-Mail-Adresse ein.")
        .required("E-Mail ist erforderlich."),
    datum: yup.date().required("Datum und Uhrzeit sind erforderlich."),
    zeit: yup.string().required("Uhrzeit ist erforderlich."),
    personen: yup
        .number()
        .min(1, "Mindestens eine Person.")
        .required("Anzahl der Personen ist erforderlich."),
    kommentar: yup.string(),
});

const ReservationForm = () => {
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const {
        handleSubmit,
        control,
        register,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    const isDateDisabled = (date) => {
        const month = date.getMonth(); // January = 0, February = 1, ..., December = 11
        const day = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6

        if (day === 1) {
            return true;
        }

        if ((month <= 3 || month >= 9) && day === 2) {
            return true;
        }

        return false;
    };

    const isBlockedTimeSlot = (selectedDate, time) => {
        if (!selectedDate || !time) {
            return false;
        }

        const day = selectedDate.getDate();
        const month = selectedDate.getMonth() + 1;
        const [hourRaw, minuteRaw] = time.split(":");
        const totalMinutes = Number(hourRaw) * 60 + Number(minuteRaw);

        if (month === 7 && day === 10 && totalMinutes >= 15 * 60) {
            return true;
        }

        if (
            month === 7 &&
            day === 12 &&
            totalMinutes >= 11 * 60 + 30 &&
            totalMinutes <= 16 * 60
        ) {
            return true;
        }

        return false;
    };

    const generateTimeOptions = (selectedDate) => {
        const options = [];
        for (let hour = 11; hour <= 21; hour++) {
            if (hour > 11) {
                const time = `${hour}:00`;
                if (!isBlockedTimeSlot(selectedDate, time)) {
                    options.push(time);
                }
            }
            if (hour < 21) {
                const time = `${hour}:30`;
                if (!isBlockedTimeSlot(selectedDate, time)) {
                    options.push(time);
                }
            }
        }
        return options;
    };

    const onSubmit = async (data) => {
        const payload = {
            ...data,
        };

        setLoading(true);
        try {
            await axios.post("/api/public/reservation", payload);
            setSuccessMessage(
                "Reservierungsanfrage erfolgreich gesendet. Wir freuen uns, Sie begrüßen zu dürfen!",
            );
            reset();
        } catch (error) {
            console.error("Fehler bei der Reservierung:", error);
            setSuccessMessage(
                error?.response?.data?.message ||
                    "Fehler bei der Reservierung. Bitte versuchen Sie es später erneut.",
            );
        } finally {
            setLoading(false);
        }
    };

    const renderTimeInput = () => {
        const selectedDate = watch("datum");
        const times = generateTimeOptions(selectedDate);

        return (
            <select
                className="rounded-md p-2 w-full bg-white border border-gray-400"
                {...register("zeit")}
            >
                <option value="" disabled>
                    {times.length > 0
                        ? "Wählen Sie eine Uhrzeit"
                        : "Keine Uhrzeiten verfügbar"}
                </option>
                {times.map((time) => (
                    <option key={time} value={time}>
                        {time}
                    </option>
                ))}
            </select>
        );
    };

    return (
        <Box
            sx={{ maxWidth: 500, mx: "auto", fontFamily: "Arial, sans-serif" }}
            className="mt-16 mb-32 mx-4 bg-white p-4 rounded-lg shadow-md"
        >
            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {successMessage}
                </Alert>
            )}
            <form
                onSubmit={handleSubmit(onSubmit)}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                }}
            >
                {/* First Name */}
                <TextField
                    label="Vorname"
                    {...register("vorname")}
                    error={!!errors.vorname}
                    helperText={errors.vorname?.message}
                />

                {/* Last Name */}
                <TextField
                    label="Nachname"
                    {...register("nachname")}
                    error={!!errors.nachname}
                    helperText={errors.nachname?.message}
                />

                {/* Phone */}
                <TextField
                    label="Telefon"
                    {...register("telefon")}
                    error={!!errors.telefon}
                    helperText={errors.telefon?.message}
                />

                {/* Email */}
                <TextField
                    label="E-Mail"
                    {...register("email")}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                />

                {/* Material UI Date Picker */}
                <Controller
                    name="datum"
                    control={control}
                    render={({ field }) => (
                        <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={deLocale}
                        >
                            <DatePicker
                                label="Datum wählen"
                                {...field}
                                shouldDisableDate={isDateDisabled}
                                format="dd.MM.yyyy"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={!!errors.datum}
                                        helperText={errors.datum?.message}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    )}
                />

                {/* Custom Time Picker */}
                <Controller
                    name="zeit"
                    control={control}
                    render={() => (
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Uhrzeit wählen:
                            </label>
                            {renderTimeInput()}
                        </div>
                    )}
                />

                {/* Number of People */}
                <TextField
                    label="Anzahl der Personen"
                    type="number"
                    {...register("personen")}
                    error={!!errors.personen}
                    helperText={errors.personen?.message}
                    inputProps={{ min: 1 }}
                />

                {/* Comment */}
                <TextField
                    multiline
                    label="Kommentar (optional)"
                    {...register("kommentar")}
                    error={!!errors.kommentar}
                    helperText={errors.kommentar?.message}
                />

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={loading}
                    className="bg-enedemgreen rounded-md text-white font-semibold py-2"
                >
                    {loading ? "Wird gesendet..." : "Reservieren"}
                </button>
            </form>
        </Box>
    );
};

export default ReservationForm;
